'use client';

/**
 * LUCAT CONNECT — Home Mode Context
 * Gestion du mode d'accueil : "essential" | "complete"
 * Persistance via localStorage (clé : lucat-senior-home-mode)
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type HomeMode = 'essential' | 'complete';

const STORAGE_KEY = 'lucat-senior-home-mode';

export interface HomeModeContextValue {
  /** Mode actuel, null = aucun choix effectué (afficher l'écran de sélection) */
  homeMode: HomeMode | null;
  /** true tant que la valeur n'a pas été lue depuis localStorage */
  loading: boolean;
  setHomeMode: (mode: HomeMode) => void;
  resetHomeMode: () => void;
}

const HomeModeContext = createContext<HomeModeContextValue | null>(null);

export function useHomeMode(): HomeModeContextValue {
  const ctx = useContext(HomeModeContext);
  if (!ctx) throw new Error('useHomeMode must be used inside HomeModeProvider');
  return ctx;
}

export function HomeModeProvider({ children }: { children: React.ReactNode }) {
  const [homeMode, setHomeModeState] = useState<HomeMode | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore preference from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') {
      setLoading(false);
      return;
    }
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'essential' || stored === 'complete') {
        setHomeModeState(stored);
      }
    } catch {
      // localStorage unavailable — stay null
    }
    setLoading(false);
  }, []);

  const setHomeMode = useCallback((mode: HomeMode) => {
    setHomeModeState(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Silencieux
    }
  }, []);

  const resetHomeMode = useCallback(() => {
    setHomeModeState(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Silencieux
    }
  }, []);

  return (
    <HomeModeContext.Provider value={{ homeMode, loading, setHomeMode, resetHomeMode }}>
      {children}
    </HomeModeContext.Provider>
  );
}
